import { useEffect, useState } from 'react';
import {
  runOnJS,
  useAnimatedReaction,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import type { OrderStatusMapProps } from '../../types';

// ────────────────────────────────────────────────────────────────────────────────

export const useAnimatedPins = (props: OrderStatusMapProps) => {
  const {
    locationLatitude,
    locationLongitude,
    customerLatitude,
    customerLongitude,
    deliveryAddressLatitude,
    deliveryAddressLongitude,
    courierLatitude,
    courierLongitude,
  } = props;

  const hasCourierGeolocation = courierLatitude && courierLongitude;
  const courierGeolocation = hasCourierGeolocation
    ? { courierLatitude, courierLongitude }
    : {};

  return {
    locationLatitude: useAnimatedCoordinate(locationLatitude),
    locationLongitude: useAnimatedCoordinate(locationLongitude),
    customerLatitude: useAnimatedCoordinate(customerLatitude),
    customerLongitude: useAnimatedCoordinate(customerLongitude),
    deliveryAddressLatitude: useAnimatedCoordinate(deliveryAddressLatitude),
    deliveryAddressLongitude: useAnimatedCoordinate(deliveryAddressLongitude),
    ...courierGeolocation,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ANIMATION_DURATION = 100;

const useAnimatedCoordinate = (target?: number): number | undefined => {
  const shared = useSharedValue(target ?? 0);
  const [current, setCurrent] = useState(target);

  useEffect(() => {
    if (target === undefined) return;

    shared.value = withTiming(target, { duration: ANIMATION_DURATION });
  }, [shared, target]);

  useAnimatedReaction(
    () => shared.value,
    (value) => {
      runOnJS(setCurrent)(value);
    },
    [shared],
  );

  if (target === undefined) return undefined;

  return current ?? target;
};
